import type { Chapter, SprintEntity } from '@data';
import type { ReportEntity } from '@data/utils/types';

import { chapterGroups, peopleAcross, rateIn, teamAt, type VersionTeam } from './budget';
import { fractionalRate, isFractional, type FractionalPerson } from './fractional';
import { workforceCost, type HoursBySprint, type InsightSprint } from './insights';
import { hoursByMember } from './spend';

/** One stack per chapter, plus the bought-in rows, in euros per sprint. */
export interface SpendSeries {
  key: string;
  label: string;
  values: number[];
}

/** The year's people and each sprint's rates, read the way the Spend tab reads them. */
const pricing = ({
  year,
  sprints,
  teams,
  roster,
}: {
  year: string;
  sprints: SprintEntity[];
  teams: VersionTeam[];
  roster: FractionalPerson[];
}) => {
  // Every version the year touches, or the nearest when the year sits outside them, as Spend reads it.
  const touching = teams.filter(
    team => team.version.startDate <= `${year}-12-31` && team.version.endDate >= `${year}-01-01`,
  );
  const nearest = `${year}-12-31` < (teams[0]?.version.startDate ?? '') ? teams.slice(0, 1) : teams.slice(-1);
  const people = peopleAcross(touching.length ? touching : nearest);
  const columns: InsightSprint[] = sprints.map(sprint => ({
    id: sprint.id,
    number: sprint.number,
    startDate: sprint.startDate.slice(0, 10),
  }));
  const teamBySprint = Object.fromEntries(columns.map(sprint => [sprint.id, teamAt(teams, sprint.startDate)]));
  const rateAt = (sprintId: string, personId: string): number | null =>
    isFractional(roster, personId) ? fractionalRate(roster, personId) : rateIn(teamBySprint[sprintId], personId);
  return { people, columns, rateAt };
};

/** The same pricing as the Spend tab: each sprint at the version covering it, bought-in rows at their own rate. */
export const spendSeries = ({
  year,
  sprints,
  reportsBySprint,
  teams,
  chapters,
  roster,
  boughtInHours,
  labels,
}: {
  year: string;
  sprints: SprintEntity[];
  reportsBySprint: Record<string, ReportEntity[]>;
  teams: VersionTeam[];
  chapters: Chapter[];
  roster: FractionalPerson[];
  boughtInHours: (sprintId: string) => Record<string, number>;
  labels: { unfiled: string; fractional: string };
}): SpendSeries[] => {
  const { people, columns, rateAt } = pricing({ year, sprints, teams, roster });
  const hours: HoursBySprint = Object.fromEntries(
    sprints.map(sprint => [
      sprint.id,
      { ...hoursByMember(reportsBySprint[sprint.id] ?? []), ...boughtInHours(sprint.id) },
    ]),
  );
  const groups = [
    ...chapterGroups(people, chapters, labels.unfiled),
    ...(roster.length ? [{ key: 'fractional', label: labels.fractional, members: roster }] : []),
  ];
  return groups
    .map(group => ({
      key: group.key,
      label: group.label,
      values: columns.map(sprint => workforceCost([sprint], hours, group.members, rateAt)),
    }))
    .filter(series => series.values.some(value => value > 0));
};

/** What each sprint's budgeted hours come to at the same rates; null where nothing was budgeted. */
export const budgetCosts = ({
  year,
  sprints,
  teams,
  roster,
  budgetHours,
}: {
  year: string;
  sprints: SprintEntity[];
  teams: VersionTeam[];
  roster: FractionalPerson[];
  budgetHours: (sprintId: string) => Record<string, number>;
}): Record<string, number | null> => {
  const { people, columns, rateAt } = pricing({ year, sprints, teams, roster });
  return Object.fromEntries(
    columns.map(sprint => {
      const planned = budgetHours(sprint.id);
      if (!Object.values(planned).some(value => value > 0)) return [sprint.id, null];
      return [sprint.id, workforceCost([sprint], { [sprint.id]: planned }, [...people, ...roster], rateAt)];
    }),
  );
};
